import React from 'react';
import { Box, Typography } from '@mui/material';
import { MaximizeIcon } from '../../../Icons/DashboardIcons';

/**
 * Reusable Chart Card Component with maximize button
 * 
 * @param {string} title - Title to display in the card header
 * @param {function} onMaximize - Function to call when maximize button is clicked
 * @param {string} hoverColor - Border color on hover (optional, defaults to #c144ff)
 * @param {object} height - Height object with responsive values
 * @param {React.ReactNode} children - The chart content
 * @param {React.ReactNode} footer - Optional footer content (like legends or navigation)
 * @param {object} contentProps - Additional props for the inner content box
 */
const ChartCard = ({
    title,
    onMaximize,
    hoverColor = '#c144ff',
    height = { xs: '320px', sm: '380px', md: '480px' },
    children,
    footer,
    contentProps = {},
}) => {
    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: { xs: '8px', md: '12px' },
                border: '1px solid #e6e6e6',
                boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                height,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    padding: { xs: '10px', md: '12px' },
                    borderBottom: '1px solid #e6e6e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: '14px', md: '16px' },
                        fontWeight: 500,
                        fontFamily: '"Inter", sans-serif',
                        color: '#333333',
                    }}
                >
                    {title}
                </Typography>
                <Box
                    onClick={onMaximize}
                    sx={{
                        padding: { xs: '6px', md: '8px' },
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: '#f5f5f5',
                            borderColor: hoverColor,
                        },
                    }}
                >
                    <MaximizeIcon />
                </Box>
            </Box>
            {/* Content */}
            <Box
                sx={{
                    padding: { xs: '10px', md: '12px' },
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: '20px',
                        padding: { xs: '12px', md: '20px' },
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#ffffff',
                        ...contentProps,
                    }}
                >
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        {children}
                    </Box>
                    {footer && (
                        <Box sx={{ mt: { xs: 1, md: 2 } }}>
                            {footer}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ChartCard;
